import { afterNextRender, ChangeDetectionStrategy, Component, inject, NgZone, signal } from '@angular/core';

import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';

import { IonApp, IonRouterOutlet, NavController, Platform } from '@ionic/angular/standalone';

import { AppInsightsService } from '@core/services/app-insights.service';
import { InitService } from '@core/services/init.service';
import { KeyboardScrollService } from '@core/services/keyboard-scroll.service';
import { UiBrandingService } from '@core/services/ui-branding.service';

import { OverlaySplash } from '@shared/components/overlay-splash/overlay-splash';
import type { OverlaySplashBranding } from '@shared/components/overlay-splash/overlay-splash.types';

const DEFAULT_SPLASH_DURATION_MS = 6000;
const SPLASH_FADE_OUT_MS = 300;

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonApp, IonRouterOutlet, OverlaySplash],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  private readonly platform = inject(Platform);
  private readonly brandingService = inject(UiBrandingService);
  private readonly keyboardScrollService = inject(KeyboardScrollService);
  private readonly navController = inject(NavController);
  private readonly zone = inject(NgZone);
  private readonly initService = inject(InitService);
  private lastHandledUrl: string | null = null;
  private lastHandledTime = 0;
  private readonly appInsightsService = inject(AppInsightsService);

  protected readonly isInitialized = this.initService.isInitialized;
  protected readonly showSplash = signal(Capacitor.isNativePlatform());
  protected readonly splashFadingOut = signal(false);

  constructor() {
    this.appInsightsService.initAppInsights();
    this.keyboardScrollService.init();
    this.initUniversalLinks();
    this.platform.ready().then(() => this.initService.initApp());

    if (this.showSplash()) {
      const splashConfig = this.getEntrySplashConfig();
      const duration = splashConfig?.minDuration ?? DEFAULT_SPLASH_DURATION_MS;
      const minDuration = new Promise((resolve) => setTimeout(resolve, duration));

      if (splashConfig?.backgroundColor) {
        document.body.style.backgroundColor = splashConfig.backgroundColor;
      }

      // Hide native splash once Angular has rendered the web splash.
      // Small delay lets the browser fully paint the web splash before the native one fades.
      afterNextRender(() => {
        setTimeout(() => {
          SplashScreen.hide({ fadeOutDuration: 500 }).catch(console.error);
        }, 100);
      });

      Promise.all([this.platform.ready(), minDuration])
        .then(() => {
          // Trigger fade-out animation, then remove from DOM after it completes
          this.splashFadingOut.set(true);
          setTimeout(() => {
            this.showSplash.set(false);
            document.body.style.backgroundColor = '';
          }, SPLASH_FADE_OUT_MS);
        })
        .catch((error) => {
          console.error('Splash screen init failed during platform.ready():', error);
          this.showSplash.set(false);
          document.body.style.backgroundColor = '';
        });
    }
  }

  private initUniversalLinks(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => this.handleDeepLink(event.url));
    });

    App.getLaunchUrl().then((result) => {
      if (result?.url) {
        this.zone.run(() => this.handleDeepLink(result.url));
      }
    });

    // Native workaround for warm-start Universal Links (Capacitor doesn't forward them reliably)
    window.addEventListener('nativeUniversalLink', ((event: CustomEvent<{ url: string }>) => {
      this.zone.run(() => this.handleDeepLink(event.detail.url));
    }) as EventListener);
  }

  private handleDeepLink(url: string): void {
    const now = Date.now();
    if (url === this.lastHandledUrl && now - this.lastHandledTime < 5000) return;
    this.lastHandledUrl = url;
    this.lastHandledTime = now;

    const slug = `/${url.replace(/^https:\/\/[^\/]*\//, '')}`;
    if (slug) {
      this.navController.navigateRoot(slug);
    }
  }

  private getEntrySplashConfig(): OverlaySplashBranding | undefined {
    const branding = this.brandingService.branding();
    const components = branding?.['components'] as Record<string, unknown> | undefined;
    return components?.['entrysplash'] as OverlaySplashBranding | undefined;
  }
}
